import type { QueryClient } from "@tanstack/react-query";
import { createContext, useContext, useSyncExternalStore } from "react";

import { ConnectionMonitor, type ConnectionSnapshot } from "./connection";
import { InstallController, type InstallSnapshot } from "./install";
import { resyncAfterReconnect } from "./resync";
import { ServiceWorkerUpdater, type UpdateSnapshot } from "./serviceWorker";
import { UnsavedChangesTracker } from "./unsavedChanges";

export interface PwaServices {
  connection: ConnectionMonitor;
  updater: ServiceWorkerUpdater;
  installer: InstallController;
  unsaved: UnsavedChangesTracker;
}

export function createPwaServices({
  queryClient,
  onSessionExpired,
}: {
  queryClient: QueryClient;
  onSessionExpired: () => void;
}): PwaServices {
  return {
    connection: new ConnectionMonitor({
      resync: () => resyncAfterReconnect(queryClient, onSessionExpired),
    }),
    updater: new ServiceWorkerUpdater(),
    installer: new InstallController(),
    unsaved: new UnsavedChangesTracker(),
  };
}

export const PwaContext = createContext<PwaServices | null>(null);

export function usePwa(): PwaServices {
  const services = useContext(PwaContext);
  if (services === null) {
    throw new Error("usePwa должен вызываться внутри PwaProvider.");
  }

  return services;
}

export function useConnection(): ConnectionSnapshot {
  const { connection } = usePwa();

  return useSyncExternalStore(connection.subscribe, connection.getSnapshot);
}

export function useServiceWorkerUpdate(): UpdateSnapshot {
  const { updater } = usePwa();

  return useSyncExternalStore(updater.subscribe, updater.getSnapshot);
}

export function useInstall(): InstallSnapshot {
  const { installer } = usePwa();

  return useSyncExternalStore(installer.subscribe, installer.getSnapshot);
}

/** То же, что `usePwa`, но без ошибки вне провайдера: кнопка установки просто не показывается. */
export function useOptionalPwa(): PwaServices | null {
  return useContext(PwaContext);
}

const noSubscription = () => () => undefined;

/** Состояние установки, если провайдер есть; иначе null. */
export function useOptionalInstall(): InstallSnapshot | null {
  const pwa = useContext(PwaContext);

  return useSyncExternalStore(
    pwa?.installer.subscribe ?? noSubscription,
    () => pwa?.installer.getSnapshot() ?? null,
  );
}
